import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Boxes, Tag, SlidersHorizontal, CalendarRange, Calculator, Truck, ClipboardList, Ship, CreditCard,
} from 'lucide-react';
import { ListSection, ListRow, PrimaryButton, AlertBadge } from '../../components/RowList';
import SubmitBudgetButton from '../../components/SubmitBudgetButton';
import { requestedStockService } from '../../services/requestedStockService';

const Purchase = () => {
  const navigate = useNavigate();

  // How many stock codes planners are waiting on. Drives the red marker on the
  // Requested Stock Codes row — and whether that section appears at all. An
  // empty queue is not news, so the section stays out of the way until there is
  // something in it.
  const [pending, setPending] = useState(0);

  useEffect(() => {
    let alive = true;
    requestedStockService.countPending()
      .then((r) => { if (alive && r?.success) setPending(r.data.pending || 0); })
      .catch(() => {});   // a badge that cannot load must not break the page
    return () => { alive = false; };
  }, []);

  return (
    <div className="h-full overflow-y-auto font-sans bg-slate-100 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto px-6 py-10">

        <div className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Purchase</h1>
            <p className="mt-2 text-base text-slate-500 dark:text-slate-400">
              Manage suppliers and purchase pricing for stocked and non-stocked items.
            </p>
          </div>
          <SubmitBudgetButton module="MOD_2_SUPPLY" />
        </div>

        {/* Requests from the rest of the business. Shown ONLY when the queue is
            not empty, and first when it is — a section that exists permanently
            to say "nothing waiting" is a section people stop reading. */}
        {pending > 0 && (
          <ListSection title="Requests">
            <ListRow
              icon={ClipboardList}
              title="Requested Stock Codes"
              alert={<AlertBadge>{pending} awaiting</AlertBadge>}
              description={`${pending} stock code${pending === 1 ? '' : 's'} requested from the Bill of Materials ${
                pending === 1 ? 'is' : 'are'
              } waiting to be completed and pushed to the item master.`}
              actions={<PrimaryButton onClick={() => navigate('/purchase/requested-stock-codes')}>Open</PrimaryButton>}
            />
          </ListSection>
        )}

        <ListSection title="Pricing">
          <ListRow
            icon={Boxes}
            title="Stocked Pricing"
            description="Build the landed cost of procurement (stocked) items per warehouse and period, and set the supplier each item is bought from."
            actions={<PrimaryButton onClick={() => navigate('/purchase/stocked')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={CalendarRange}
            title="Monthly Pricing"
            description="See the landed price of every stocked item by month for a warehouse and product class. Typing a new price back-solves the base purchase price for that month."
            actions={<PrimaryButton onClick={() => navigate('/purchase/monthly-pricing')}>Open</PrimaryButton>}
          />
          <ListRow
            icon={Tag}
            title="Non-Stocked Pricing"
            description="Pricing for non-stocked (made-in / finished-goods) items."
            actions={<PrimaryButton onClick={() => navigate('/purchase/non-stocked')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Costing">
          <ListRow
            icon={Calculator}
            title="Procurement Costing"
            description="Run the procurement pricing calculation — landed price per item, warehouse and month, with the Budget Setup pricing method applied. Results and any exceptions on two tabs."
            actions={<PrimaryButton onClick={() => navigate('/purchase/costing')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* Supplier master data lives HERE rather than under Master Data:
            procurement owns the supplier list, and it is the only module that
            uses it. Reference data, so it sits near the bottom with the other
            set-up-once screens — editing and the Excel import/export are one
            page, the same as Revenue > Customers. */}
        <ListSection title="Suppliers">
          <ListRow
            icon={Truck}
            title="Suppliers"
            description="Add, edit and retire suppliers — contact, country, invoicing currency, incoterms, payment terms and lead time. Import and export the whole list as Excel from the same page."
            actions={<PrimaryButton onClick={() => navigate('/master-data/suppliers')}>Open</PrimaryButton>}
          />
        </ListSection>

        {/* The two lookup lists the supplier record points at. Kept as one
            section directly under Suppliers rather than folded into it: they
            are reference data set up once, not records anyone maintains daily,
            and burying them inside the supplier row would hide the fact that
            the codes are editable at all. */}
        <ListSection title="Supplier Terms">
          <ListRow
            icon={Ship}
            title="Incoterms"
            description="The delivery rules suppliers are quoted on — who carries cost and risk, and up to where. Seeded with the published Incoterms 2020 eleven; edit or retire what your business does not use."
            actions={<PrimaryButton onClick={() => navigate('/master-data/incoterms')}>Open</PrimaryButton>}
          />
          {/* The route's :type segment is the MASTER_SPECS key, so it carries an
              underscore rather than the hyphen the other paths use. */}
          <ListRow
            icon={CreditCard}
            title="Payment Terms"
            description="The house list of payment terms, each with the days to pay and whether they run from the invoice, the statement or month end — what cash-flow phasing needs."
            actions={<PrimaryButton onClick={() => navigate('/master-data/payment_terms')}>Open</PrimaryButton>}
          />
        </ListSection>

        <ListSection title="Configuration">
          <ListRow
            icon={SlidersHorizontal}
            title="Budget Setup"
            description="Choose how procurement pricing rolls up: actual price per month (forecasting) or a weighted-average yearly price (budget)."
            actions={<PrimaryButton onClick={() => navigate('/purchase/budget-setup')}>Open</PrimaryButton>}
          />
        </ListSection>

      </div>
    </div>
  );
};

export default Purchase;
